// In-browser whisper-tiny via @xenova/transformers (transformers.js). Loads
// lazily — the package is multi-MB and we don't want it in the initial bundle.
// The first run downloads the model (~40MB) to the browser's cache; subsequent
// loads are near-instant.
//
// Status surface mirrors app/src/lib/whisper.ts so UI code is the same shape.

export type WhisperStatus =
  | { kind: 'idle' }
  | { kind: 'loading'; message: string }
  | { kind: 'ready' }
  | { kind: 'error'; message: string };

type Listener = (s: WhisperStatus) => void;
const listeners = new Set<Listener>();
let current: WhisperStatus = { kind: 'idle' };

export function onWhisperStatus(fn: Listener): () => void {
  listeners.add(fn);
  fn(current);
  return () => {
    listeners.delete(fn);
  };
}

function setStatus(s: WhisperStatus) {
  current = s;
  for (const l of listeners) {
    try {
      l(s);
    } catch {}
  }
}

let pipePromise: Promise<any> | null = null;

// Lazy-load transformers.js and the whisper-tiny.en pipeline. Idempotent.
export function getWhisper(): Promise<any> {
  if (pipePromise) return pipePromise;
  pipePromise = (async () => {
    setStatus({ kind: 'loading', message: 'Loading voice model (~40MB, first launch only)…' });
    try {
      const mod: any = await import('@xenova/transformers');
      // Tell transformers.js not to look for local .bin models.
      mod.env.allowLocalModels = false;
      const pipeline = await mod.pipeline(
        'automatic-speech-recognition',
        'Xenova/whisper-tiny.en',
        { quantized: true }
      );
      setStatus({ kind: 'ready' });
      return pipeline;
    } catch (e: any) {
      const msg = e?.message ?? String(e);
      setStatus({ kind: 'error', message: msg });
      // Allow retry via retryWhisper().
      pipePromise = null;
      throw e;
    }
  })();
  return pipePromise;
}

export function retryWhisper() {
  pipePromise = null;
  setStatus({ kind: 'idle' });
}

export function whisperReady(): boolean {
  return current.kind === 'ready';
}

// Transcribe a Blob. Uses a blob: URL which transformers.js fetches + decodes
// to Float32 PCM internally. Works for audio/webm, audio/ogg, audio/mp4 on
// modern browsers via the Web Audio API decoder that transformers.js calls.
export async function transcribeAudio(blob: Blob): Promise<string> {
  const pipe = await getWhisper();
  const url = URL.createObjectURL(blob);
  try {
    const out = await pipe(url);
    const text: string = (Array.isArray(out) ? out[0]?.text : out?.text) ?? '';
    return text.trim();
  } finally {
    URL.revokeObjectURL(url);
  }
}
